/**
 * Merge burst particles — soft tier-tinted dots + gold spark lines.
 * Style: light cosmetic burst (~350–400 ms), not screen-filling.
 */

/** Must match the velocity multiplier in drawMergeParticles. */
export const MERGE_PARTICLE_DRAW_VELOCITY = 28;

import {
  RUN_CONFIG,
  getFallObjectDrawSize,
  getFallSpawnBurstColor,
} from "../config/constants.js";

/**
 * @param {number} x
 * @param {number} y
 * @param {string} [tierColor] hex
 * @param {{ count?: number, seed?: number, scale?: number }} [opts]
 */
export function createMergeParticles(x, y, tierColor, opts) {
  opts = opts || {};
  var count = opts.count != null ? Math.max(4, Math.floor(opts.count)) : 10;
  var baseColor = tierColor || "#94a3b8";
  var seed = opts.seed != null ? Number(opts.seed) : Date.now();
  var scale = opts.scale != null ? Math.max(0.5, Number(opts.scale)) : 1;
  if (!isFinite(scale)) scale = 1;
  var speedScale = opts.speedScale != null ? Math.max(0.5, Number(opts.speedScale)) : scale;
  if (!isFinite(speedScale)) speedScale = scale;
  var alphaMin = opts.alphaMin != null ? Number(opts.alphaMin) : 0.75;
  var alphaMax = opts.alphaMax != null ? Number(opts.alphaMax) : 0.95;
  if (!isFinite(alphaMin)) alphaMin = 0.75;
  if (!isFinite(alphaMax)) alphaMax = 0.95;
  if (alphaMax < alphaMin) alphaMax = alphaMin;
  var brightenAmount = opts.brightenAmount != null ? Number(opts.brightenAmount) : 0;
  if (!isFinite(brightenAmount) || brightenAmount < 0) brightenAmount = 0;
  var dotColor = brightenAmount > 0 ? brightenHexColor(baseColor, brightenAmount) : baseColor;
  var sparkColor = brightenAmount > 0 ? brightenHexColor("#fff6d0", brightenAmount * 0.55) : "#fff6d0";
  var particles = [];
  var sparkleCount = Math.max(2, Math.floor(count * 0.3));
  var dotCount = count - sparkleCount;

  for (var i = 0; i < dotCount; i++) {
    var h = hash01(seed, i * 3.1);
    var angle = h * Math.PI * 2;
    var speed = (1.5 + hash01(seed, i * 5.7) * 3.5) * speedScale;
    particles.push({
      kind: "dot",
      x: x,
      y: y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 1.2 * scale,
      radius: (2 + hash01(seed, i * 7.3) * 3) * scale,
      alpha: alphaMin + hash01(seed, i * 2.9) * (alphaMax - alphaMin),
      color: dotColor,
    });
  }

  for (var j = 0; j < sparkleCount; j++) {
    var sj = dotCount + j;
    var sh = hash01(seed, sj * 4.1);
    var sAngle = sh * Math.PI * 2;
    var len = (6 + hash01(seed, sj * 6.2) * 8) * scale;
    particles.push({
      kind: "spark",
      x: x,
      y: y,
      vx: Math.cos(sAngle) * (2 + hash01(seed, sj * 8.1)) * speedScale,
      vy: Math.sin(sAngle) * (2 + hash01(seed, sj * 8.1)) * speedScale - 1 * scale,
      length: len,
      width: (1 + hash01(seed, sj * 9.4) * 0.8) * Math.max(1, scale * 0.85),
      angle: sAngle,
      alpha: Math.min(1, alphaMax),
      color: sparkColor,
    });
  }

  return particles;
}

/**
 * Runway spawn burst — tier/type tint, object-sized scale, merge-style sparks.
 * @param {number} x
 * @param {number} y
 * @param {object} obj
 * @param {number} runWidthPx
 * @param {object} [runConfig]
 * @param {{ seed?: number }} [opts]
 */
export function createFallSpawnBurstParticles(x, y, obj, runWidthPx, runConfig, opts) {
  opts = opts || {};
  var cfg = runConfig || RUN_CONFIG || {};
  var drawSize = getFallObjectDrawSize(obj, runWidthPx, cfg);
  var baseCount = cfg.FALL_SPAWN_PARTICLE_COUNT != null ? Number(cfg.FALL_SPAWN_PARTICLE_COUNT) : 14;
  if (!isFinite(baseCount) || baseCount < 4) baseCount = 14;
  var refHalf = cfg.FALL_SPAWN_PARTICLE_REF_HALF_PX != null
    ? Number(cfg.FALL_SPAWN_PARTICLE_REF_HALF_PX)
    : 18;
  if (!isFinite(refHalf) || refHalf < 6) refHalf = 18;
  var sizeScale = Math.max(0.7, Math.min(2.35, drawSize.halfExtent / refHalf));
  var count = Math.round(baseCount * (0.8 + sizeScale * 0.35));
  count = Math.max(8, Math.min(22, count));
  var color = getFallSpawnBurstColor(obj);
  var seed = opts.seed != null ? Number(opts.seed) : Date.now();
  return createMergeParticles(x, y, color, {
    count: count,
    seed: seed,
    scale: sizeScale,
  });
}

/**
 * Flask merge burst — tier tint, body-radius scale, merge-style sparks.
 * @param {number} x
 * @param {number} y
 * @param {string} [tierColor] hex
 * @param {number} [bodyRadiusPx]
 * @param {object} [runConfig]
 * @param {{ seed?: number }} [opts]
 */
export function createFlaskMergeBurstParticles(x, y, tierColor, bodyRadiusPx, runConfig, opts) {
  opts = opts || {};
  var cfg = runConfig || RUN_CONFIG || {};
  var baseCount = cfg.FLASK_MERGE_PARTICLE_COUNT != null ? Number(cfg.FLASK_MERGE_PARTICLE_COUNT) : 16;
  if (!isFinite(baseCount) || baseCount < 4) baseCount = 16;
  var refHalf = cfg.FLASK_MERGE_PARTICLE_REF_HALF_PX != null
    ? Number(cfg.FLASK_MERGE_PARTICLE_REF_HALF_PX)
    : 14;
  if (!isFinite(refHalf) || refHalf < 6) refHalf = 14;
  var radius = bodyRadiusPx != null && isFinite(bodyRadiusPx) ? bodyRadiusPx : refHalf;
  var sizeScale = Math.max(0.85, Math.min(2.25, radius / refHalf));
  var count = Math.round(baseCount * (0.9 + sizeScale * 0.35));
  count = Math.max(12, Math.min(30, count));
  var durationMs = cfg.FLASK_MERGE_PARTICLE_MS != null ? Number(cfg.FLASK_MERGE_PARTICLE_MS) : 620;
  if (!isFinite(durationMs) || durationMs <= 0) durationMs = 620;
  var outwardMult = cfg.FLASK_MERGE_PARTICLE_OUTWARD_RADIUS_MULT != null
    ? Number(cfg.FLASK_MERGE_PARTICLE_OUTWARD_RADIUS_MULT)
    : 2.1;
  if (!isFinite(outwardMult) || outwardMult < 1) outwardMult = 2.1;
  var speedBoost = cfg.FLASK_MERGE_PARTICLE_SPEED_SCALE != null
    ? Number(cfg.FLASK_MERGE_PARTICLE_SPEED_SCALE)
    : 1;
  if (!isFinite(speedBoost) || speedBoost < 1) speedBoost = 1;
  var drawVelocity = cfg.FLASK_MERGE_PARTICLE_DRAW_VELOCITY != null
    ? Number(cfg.FLASK_MERGE_PARTICLE_DRAW_VELOCITY)
    : 16;
  if (!isFinite(drawVelocity) || drawVelocity <= 0) drawVelocity = 16;
  var durationSec = durationMs / 1000;
  var minPeakSpeed = (radius * outwardMult) / (durationSec * drawVelocity);
  var speedScale = Math.max(sizeScale * speedBoost, minPeakSpeed / 5);
  var brightenAmount = cfg.FLASK_MERGE_PARTICLE_BRIGHTEN != null
    ? Number(cfg.FLASK_MERGE_PARTICLE_BRIGHTEN)
    : 0.42;
  if (!isFinite(brightenAmount) || brightenAmount < 0) brightenAmount = 0;
  var alphaMin = cfg.FLASK_MERGE_PARTICLE_ALPHA_MIN != null
    ? Number(cfg.FLASK_MERGE_PARTICLE_ALPHA_MIN)
    : 0.9;
  var alphaMax = cfg.FLASK_MERGE_PARTICLE_ALPHA_MAX != null
    ? Number(cfg.FLASK_MERGE_PARTICLE_ALPHA_MAX)
    : 1;
  if (!isFinite(alphaMin)) alphaMin = 0.9;
  if (!isFinite(alphaMax)) alphaMax = 1;
  var seed = opts.seed != null ? Number(opts.seed) : Date.now();
  return createMergeParticles(x, y, tierColor || "#94a3b8", {
    count: count,
    seed: seed,
    scale: sizeScale,
    speedScale: speedScale,
    brightenAmount: brightenAmount,
    alphaMin: alphaMin,
    alphaMax: alphaMax,
  });
}

function hash01(seed, n) {
  var s = Math.sin((seed + n * 12.9898) * 0.0001 + n * 78.233) * 43758.5453;
  return s - Math.floor(s);
}

function parseHexColor(hex) {
  if (!hex || hex.indexOf("#") !== 0 || hex.length < 7) return { r: 148, g: 163, b: 184 };
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

function brightenHexColor(hex, amount) {
  var rgb = parseHexColor(hex);
  var mix = Math.max(0, Math.min(1, amount));
  var r = Math.round(rgb.r + (255 - rgb.r) * mix);
  var g = Math.round(rgb.g + (255 - rgb.g) * mix);
  var b = Math.round(rgb.b + (255 - rgb.b) * mix);
  return "#"
    + r.toString(16).padStart(2, "0")
    + g.toString(16).padStart(2, "0")
    + b.toString(16).padStart(2, "0");
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {ReturnType<typeof createMergeParticles>} particles
 * @param {number} startMs
 * @param {number} durationMs
 * @param {number} [nowMs]
 * @param {{ drawVelocity?: number, gravity?: number, fadePower?: number, glow?: boolean, playfield?: object, origin?: { x: number, y: number }, keyPrefix?: string }} [drawOpts]
 */
export function drawMergeParticles(ctx, particles, startMs, durationMs, nowMs, drawOpts) {
  if ((!ctx && !(drawOpts && drawOpts.playfield)) || !particles || particles.length === 0) return;
  drawOpts = drawOpts || {};
  var now = nowMs != null ? nowMs : Date.now();
  var elapsed = (now - startMs) / 1000;
  var duration = Math.max(0.001, durationMs / 1000);
  if (elapsed >= duration) return;

  var progress = elapsed / duration;
  var fadePower = drawOpts.fadePower != null ? Number(drawOpts.fadePower) : 1;
  if (!isFinite(fadePower) || fadePower <= 0) fadePower = 1;
  var fade = Math.pow(Math.max(0, 1 - progress), fadePower);
  var drawVelocity = drawOpts.drawVelocity != null
    ? Number(drawOpts.drawVelocity)
    : MERGE_PARTICLE_DRAW_VELOCITY;
  if (!isFinite(drawVelocity) || drawVelocity <= 0) drawVelocity = MERGE_PARTICLE_DRAW_VELOCITY;
  var gravity = drawOpts.gravity != null ? Number(drawOpts.gravity) : 50;
  if (!isFinite(gravity) || gravity < 0) gravity = 50;

  var playfield = drawOpts.playfield;
  var origin = drawOpts.origin || { x: 0, y: 0 };
  var keyPrefix = drawOpts.keyPrefix || "pt";

  for (var i = 0; i < particles.length; i++) {
    var p = particles[i];
    var px = p.x + p.vx * elapsed * drawVelocity;
    var py = p.y + p.vy * elapsed * drawVelocity + gravity * elapsed * elapsed;
    var alpha = fade * p.alpha;
    if (alpha <= 0.02) continue;

    if (playfield && typeof playfield.effect === "function") {
      var gx = origin.x + px;
      var gy = origin.y + py;
      if (p.kind === "spark") {
        playfield.effect({
          key: keyPrefix + "-s" + i,
          kind: "spark",
          x: gx,
          y: gy,
          length: p.length * (1 - progress * 0.35),
          rotation: p.angle,
          stroke: p.width,
          color: p.color,
          alpha: alpha,
          zIndex: 35,
        });
        continue;
      }
      var radius = Math.max(0.5, p.radius * (1 - progress * 0.28));
      if (drawOpts.glow) {
        playfield.effect({
          key: keyPrefix + "-g" + i,
          kind: "glow",
          blend: "add",
          x: gx,
          y: gy,
          width: radius * 3.4,
          height: radius * 3.4,
          rgb: [255, 255, 255],
          alpha: alpha * 0.42,
          zIndex: 34,
        });
      }
      playfield.effect({
        key: keyPrefix + "-d" + i,
        kind: "glow",
        blend: "add",
        x: gx,
        y: gy,
        width: radius * 2,
        height: radius * 2,
        color: p.color,
        alpha: alpha,
        zIndex: 35,
      });
      continue;
    }

    if (!ctx) continue;

    if (p.kind === "spark") {
      var rgb = parseHexColor(p.color);
      var half = (p.length * (1 - progress * 0.35)) * 0.5;
      var cosA = Math.cos(p.angle);
      var sinA = Math.sin(p.angle);
      ctx.save();
      ctx.strokeStyle = "rgba(" + rgb.r + "," + rgb.g + "," + rgb.b + "," + alpha.toFixed(3) + ")";
      ctx.lineWidth = p.width;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(px - cosA * half, py - sinA * half);
      ctx.lineTo(px + cosA * half, py + sinA * half);
      ctx.stroke();
      ctx.restore();
      continue;
    }

    var dotRgb = parseHexColor(p.color);
    var radius = Math.max(0.5, p.radius * (1 - progress * 0.28));
    if (drawOpts.glow) {
      ctx.fillStyle = "rgba(255,255,255," + (alpha * 0.42).toFixed(3) + ")";
      ctx.beginPath();
      ctx.arc(px, py, radius * 1.7, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = "rgba(" + dotRgb.r + "," + dotRgb.g + "," + dotRgb.b + "," + alpha.toFixed(3) + ")";
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.fill();
  }
}
